import type { CabinaMessage } from './cabinaContent';
import type { RadioScoreData } from '@/components/feature/RadioScore';
import type { StreamProviderId, StreamProviderType } from '@/types/streaming';
import { getContentStats } from './contentMaster';

const contentStats = getContentStats();

export interface AdminStats {
  oyentesActuales: number;
  reproduccionesHoy: number;
  oyentesMes: number;
  mensajesCabina: number;
  livesActivos: number;
  eventosProximos: number;
  publicaciones: number;
  fotosSubidas: number;
  videosSubidos: number;
  lives: number;
  storageUsadoGB: number;
  storageTotalGB: number;
  seguidores: number;
  programasActivos: number;
  ingresosEstimados: number;
  notificacionesDisponibles: number;
}

export type TransmissionState =
  | 'transmitting'
  | 'autodj'
  | 'scheduled'
  | 'offline'
  | 'external_streaming'
  | 'rfw_streaming'
  | 'unconfigured';

export interface TransmissionStatus {
  state: TransmissionState;
  currentProgram: string;
  currentHost: string;
  listeners: number;
  startedAt: string;
  scheduledFor?: string;
  streamType: 'external' | 'rfw' | 'none';
  nextProgram?: string;
  providerId: StreamProviderId;
  providerType: StreamProviderType;
}

export type AlertType =
  | 'stream_caido'
  | 'stream_inestable'
  | 'storage_lleno'
  | 'licencia_vencer'
  | 'dominio_error'
  | 'facebook_desconectado'
  | 'youtube_desconectado'
  | 'mensajes_pendientes'
  | 'solicitudes_cancion'
  | 'plan_free_limitado'
  | 'aprobacion_pendiente';

export interface AdminAlert {
  id: string;
  type: AlertType;
  title: string;
  description: string;
  severity: 'critical' | 'warning' | 'info';
  icon: string;
  actionLabel: string;
  actionTarget: string;
}

export interface AdminStation {
  id: number;
  logoUrl: string;
  name: string;
  frequency: string;
  streamUrl: string;
  isLive: boolean;
  listeners: number;
  city: string;
  category: string;
  slogan: string;
  foundedYear: number;
}

export interface AdminActivity {
  id: number;
  type: 'foto' | 'mensaje' | 'follower' | 'comentario' | 'live' | 'evento' | 'publicacion' | 'video';
  description: string;
  detail: string;
  time: string;
  userAvatar?: string;
  userName?: string;
}

export interface AdminFrequencyStatus {
  frequency: string;
  status: 'activa' | 'reservada' | 'liberada' | 'pendiente';
  assignedDate: string;
  expiresDate: string;
}

export interface AdminPlanInfo {
  name: string;
  type: 'free' | 'pro' | 'premium';
  price: string;
  billingCycle: 'monthly' | 'annual';
  nextBilling: string;
  daysRemaining: number;
}

export interface AdminLicenseInfo {
  status: 'activa' | 'proxima_vencer' | 'vencida';
  issuedDate: string;
  expiryDate: string;
  daysRemaining: number;
  annual: boolean;
  price: string;
}

export interface AdminDomainStatus {
  domain: string;
  status: 'connected' | 'error' | 'pending' | 'not_configured';
  sslActive: boolean;
  lastChecked: string;
}

export interface AdminSocialStatus {
  facebook: { connected: boolean; pageName: string; lastSync: string };
  youtube: { connected: boolean; channelName: string; lastSync: string };
  instagram: { connected: boolean; username: string; lastSync: string };
  twitter: { connected: boolean; username: string; lastSync: string };
}

export interface AdminUpcomingEvent {
  id: number;
  title: string;
  date: string;
  time: string;
  type: string;
}

export interface ListenersDay {
  day: string;
  listeners: number;
}

export interface AdminDashboardData {
  stats: AdminStats;
  station: AdminStation;
  frequency: AdminFrequencyStatus;
  plan: AdminPlanInfo;
  license: AdminLicenseInfo;
  domain: AdminDomainStatus;
  social: AdminSocialStatus;
  upcomingEvents: AdminUpcomingEvent[];
  cabinaMessages: CabinaMessage[];
  recentActivity: AdminActivity[];
  alerts: AdminAlert[];
  radioScore: RadioScoreData;
  listenersLast7Days: ListenersDay[];
  transmission: TransmissionStatus;
}

export const adminAlerts: AdminAlert[] = [
  {
    id: 'alert-stream',
    type: 'stream_caido',
    title: 'Stream caído',
    description: 'Tu stream se desconectó hace 2 horas. Los oyentes no pueden escucharte. Si pasan 24h recibirás un aviso formal del sistema.',
    severity: 'critical',
    icon: 'ri-signal-wifi-error-line',
    actionLabel: 'Probar Stream',
    actionTarget: 'probar-stream',
  },
  {
    id: 'alert-licencia',
    type: 'licencia_vencer',
    title: 'Licencia próxima a vencer',
    description: 'Tu licencia anual vence en 12 días. Renová ahora para evitar la suspensión del servicio.',
    severity: 'warning',
    icon: 'ri-calendar-check-line',
    actionLabel: 'Renovar licencia',
    actionTarget: 'renovar-licencia',
  },
  {
    id: 'alert-storage',
    type: 'storage_lleno',
    title: 'Storage casi lleno',
    description: 'Estás usando el 82% de tu almacenamiento. Solo te quedan 1.8 GB libres. Liberá espacio o mejorá tu plan.',
    severity: 'warning',
    icon: 'ri-hard-drive-2-line',
    actionLabel: 'Ver storage',
    actionTarget: 'ver-storage',
  },
  {
    id: 'alert-dominio',
    type: 'dominio_error',
    title: 'Dominio con error',
    description: 'El dominio radioelnorte.com no está respondiendo. Verificá la configuración DNS.',
    severity: 'critical',
    icon: 'ri-global-line',
    actionLabel: 'Verificar dominio',
    actionTarget: 'verificar-dominio',
  },
  {
    id: 'alert-facebook',
    type: 'facebook_desconectado',
    title: 'Facebook desconectado',
    description: 'La conexión con Facebook expiró. Reconectá para volver a publicar automáticamente.',
    severity: 'warning',
    icon: 'ri-facebook-circle-line',
    actionLabel: 'Reconectar',
    actionTarget: 'reconectar-facebook',
  },
  {
    id: 'alert-youtube',
    type: 'youtube_desconectado',
    title: 'YouTube desconectado',
    description: 'La transmisión en vivo no se está enviando a YouTube. Reconectá tu canal.',
    severity: 'warning',
    icon: 'ri-youtube-line',
    actionLabel: 'Reconectar',
    actionTarget: 'reconectar-youtube',
  },
  {
    id: 'alert-stream-inestable',
    type: 'stream_inestable',
    title: 'Stream inestable',
    description: 'Se detectaron cortes intermitentes en tu transmisión. La calidad del audio puede verse afectada.',
    severity: 'warning',
    icon: 'ri-error-warning-line',
    actionLabel: 'Verificar conexión',
    actionTarget: 'probar-stream',
  },
  {
    id: 'alert-plan',
    type: 'plan_free_limitado',
    title: 'Plan Free limitado',
    description: 'Tu plan Free no incluye fotos ni videos. Mejorá a Pro para desbloquear todo.',
    severity: 'info',
    icon: 'ri-vip-line',
    actionLabel: 'Mejorar plan',
    actionTarget: 'mejorar-plan',
  },
];

export const adminDashboardData: AdminDashboardData = {
  stats: {
    oyentesActuales: contentStats.oyentesActuales,
    reproduccionesHoy: contentStats.reproduccionesHoy,
    oyentesMes: contentStats.oyentesMes,
    mensajesCabina: contentStats.mensajesCabina,
    livesActivos: contentStats.livesActivos,
    eventosProximos: contentStats.eventosProximos,
    publicaciones: contentStats.publicaciones,
    fotosSubidas: contentStats.fotosSubidas,
    videosSubidos: contentStats.videosSubidos,
    lives: contentStats.lives,
    storageUsadoGB: contentStats.storageUsadoGB,
    storageTotalGB: contentStats.storageTotalGB,
    seguidores: contentStats.seguidores,
    programasActivos: contentStats.programasActivos,
    ingresosEstimados: contentStats.ingresosEstimados,
    notificacionesDisponibles: contentStats.notificacionesDisponibles,
  },
  frequency: {
    frequency: '94.70 RFW',
    status: 'activa',
    assignedDate: '2018-03-15',
    expiresDate: '2027-03-15',
  },
  plan: {
    name: 'Pro',
    type: 'pro',
    price: '$29.99/mes',
    billingCycle: 'monthly',
    nextBilling: '2026-07-01',
    daysRemaining: 19,
  },
  license: {
    status: 'proxima_vencer',
    issuedDate: '2025-06-24',
    expiryDate: '2026-06-24',
    daysRemaining: 12,
    annual: true,
    price: '$299.90/año',
  },
  domain: {
    domain: 'radioelnorte.com',
    status: 'error',
    sslActive: false,
    lastChecked: '2026-06-11T10:30:00',
  },
  social: {
    facebook: {
      connected: false,
      pageName: 'Radio Nueva Actitud',
      lastSync: '2026-05-28',
    },
    youtube: {
      connected: false,
      channelName: 'Radio Nueva Actitud Oficial',
      lastSync: '2026-05-15',
    },
    instagram: {
      connected: true,
      username: '@radionuevaactitud',
      lastSync: '2026-06-11T10:00:00',
    },
    twitter: {
      connected: true,
      username: '@RNActitud',
      lastSync: '2026-06-11T09:45:00',
    },
  },
  upcomingEvents: [
    {
      id: 1,
      title: 'Festival Parque Central 2026',
      date: '2026-06-14',
      time: '16:00',
      type: 'festival',
    },
    {
      id: 2,
      title: 'Entrevista con Shakira',
      date: '2026-06-18',
      time: '10:00',
      type: 'entrevista',
    },
    {
      id: 3,
      title: 'Concierto Solidario RN',
      date: '2026-06-22',
      time: '19:00',
      type: 'concierto',
    },
    {
      id: 4,
      title: 'Maratón Pop 12 Horas',
      date: '2026-06-28',
      time: '08:00',
      type: 'maraton',
    },
  ],
  alerts: adminAlerts,
  station: {
    id: 1,
    logoUrl:
      'https://readdy.ai/api/search-image?query=Professional%20radio%20station%20logo%20design%20circular%20format%2C%20modern%20broadcast%20brand%20identity%2C%20warm%20golden%20and%20amber%20colors%2C%20sound%20wave%20abstract%20symbol%2C%20clean%20minimal%20corporate%20style%2C%20high%20contrast%20graphic%20mark&width=200&height=200&seq=admin-logo-1&orientation=squarish',
    name: 'Radio Nueva Actitud',
    frequency: '94.70 RFW',
    streamUrl: 'https://stream.nuevaactitud.com/live',
    isLive: true,
    listeners: 1284,
    city: 'Santa Rosa, CA',
    category: 'Pop / Variado',
    slogan: 'La nueva frecuencia de la radio',
    foundedYear: 2018,
  },
  cabinaMessages: [
    {
      id: 1,
      from: 'María G.',
      text: '¡Buenas tardes Carlos! Saludando desde Santa Rosa, excelente programa hoy.',
      time: '14:32',
      type: 'shoutout',
      avatarUrl:
        'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20friendly%20woman%20with%20warm%20smile%2C%20natural%20lighting%2C%20simple%20clean%20background%2C%20casual%20portrait%20photography&width=80&height=80&seq=admin-avatar-maria&orientation=squarish',
    },
    {
      id: 2,
      from: 'José L.',
      text: 'Pongan algo de Shakira por favor, ¡las nuevas son buenísimas!',
      time: '14:33',
      type: 'song_request',
      avatarUrl:
        'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20man%20in%20his%2030s%20with%20friendly%20expression%2C%20outdoor%20natural%20light%2C%20casual%20style%2C%20simple%20background%20portrait&width=80&height=80&seq=admin-avatar-jose&orientation=squarish',
    },
    {
      id: 3,
      from: 'Ana R.',
      text: 'Un saludo para toda la gente de California que nos escucha. ¡Vamos con todo!',
      time: '14:34',
      type: 'shoutout',
    },
    {
      id: 4,
      from: 'Pedro M.',
      text: '¿Cuándo es el próximo concierto que van a transmitir? Me encantó el del parque.',
      time: '14:36',
      type: 'message',
    },
    {
      id: 5,
      from: 'Lucía F.',
      text: 'Quiero participar en el concurso de entradas para el festival. ¡Cuenten conmigo!',
      time: '14:38',
      type: 'contest',
    },
    {
      id: 6,
      from: 'Óscar T.',
      text: 'Ya mandé mi mensaje de voz. Espero que lo pongan al aire.',
      time: '14:46',
      type: 'voice',
    },
    {
      id: 7,
      from: 'Diego C.',
      text: 'Saludos desde Los Ángeles. Soy fiel oyente desde hace 3 años. ¡Los quiero!',
      time: '14:40',
      type: 'shoutout',
    },
    {
      id: 8,
      from: 'Valentina S.',
      text: 'Carlos ¿puedes mandar un saludo a mi hija que cumple 15 años hoy? Se llama Sofía.',
      time: '14:43',
      type: 'shoutout',
    },
  ],
  recentActivity: [
    {
      id: 1,
      type: 'foto',
      description: 'Nueva foto subida',
      detail: 'En plena transmisión en vivo desde la cabina',
      time: 'Hace 12 minutos',
      userName: 'Carlos Mendoza',
    },
    {
      id: 2,
      type: 'mensaje',
      description: 'Nuevo mensaje en Cabina',
      detail: 'María G. envió un saludo desde Santa Rosa',
      time: 'Hace 18 minutos',
      userName: 'María G.',
      userAvatar:
        'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20friendly%20woman%20with%20warm%20smile%2C%20natural%20lighting%2C%20simple%20clean%20background%2C%20casual%20portrait%20photography&width=40&height=40&seq=admin-activity-maria&orientation=squarish',
    },
    {
      id: 3,
      type: 'follower',
      description: 'Nuevo seguidor',
      detail: '@radiofan_carlos empezó a seguir la estación',
      time: 'Hace 25 minutos',
    },
    {
      id: 4,
      type: 'comentario',
      description: 'Nuevo comentario',
      detail: 'Ana R. comentó en la publicación "Tardes de Pop"',
      time: 'Hace 32 minutos',
      userName: 'Ana R.',
    },
    {
      id: 5,
      type: 'live',
      description: 'Live iniciado',
      detail: 'Tardes de Pop con Carlos Mendoza está al aire',
      time: 'Hace 34 minutos',
    },
    {
      id: 6,
      type: 'follower',
      description: 'Nuevo seguidor',
      detail: '@musiclover_2026 empezó a seguir la estación',
      time: 'Hace 40 minutos',
    },
    {
      id: 7,
      type: 'video',
      description: 'Nuevo video subido',
      detail: 'Concierto en Vivo: Parque Central',
      time: 'Hace 1 hora',
    },
    {
      id: 8,
      type: 'publicacion',
      description: 'Nueva publicación',
      detail: '¡CONCURSO SORPRESA! 2 pases VIP para el festival',
      time: 'Hace 2 horas',
    },
    {
      id: 9,
      type: 'evento',
      description: 'Evento creado',
      detail: 'Festival de música del Parque Central - 14 Jun',
      time: 'Hace 3 horas',
    },
    {
      id: 10,
      type: 'follower',
      description: 'Nuevo seguidor',
      detail: '@dj_sunset empezó a seguir la estación',
      time: 'Hace 4 horas',
    },
    {
      id: 11,
      type: 'comentario',
      description: 'Nuevo comentario',
      detail: 'José L. comentó en la foto del backstage',
      time: 'Hace 5 horas',
    },
    {
      id: 12,
      type: 'mensaje',
      description: 'Nuevo mensaje en Cabina',
      detail: 'Óscar T. envió un mensaje de voz',
      time: 'Hace 6 horas',
    },
  ],
  radioScore: {
    score: 72,
    state: 'muy_activa',
    factors: {
      streamActivo: 85,
      publicaciones: 70,
      eventos: 65,
      respuestasCabina: 75,
      lives: 60,
      interaccion: 78,
    },
  },
  listenersLast7Days: [
    { day: 'Lun', listeners: 11420 },
    { day: 'Mar', listeners: 12850 },
    { day: 'Mié', listeners: 10980 },
    { day: 'Jue', listeners: 14300 },
    { day: 'Vie', listeners: 16200 },
    { day: 'Sáb', listeners: 13450 },
    { day: 'Dom', listeners: 9890 },
  ],
  transmission: {
    state: 'transmitting',
    currentProgram: 'Tardes de Pop',
    currentHost: 'Carlos Mendoza',
    listeners: 1284,
    startedAt: '2026-07-04T14:00:00',
    streamType: 'external',
    nextProgram: 'Noches de Rock',
    providerId: 'icecast',
    providerType: 'direct_server',
  },
};